/**
 * 13.11 (migration 0263; D-10, D-12) — Paelode provisions an operator company's MQTT gateways
 * (/v1/platform/operator-companies/:tenantId/mqtt-gateways). Adding and rotating answer the
 * certificate and private key once.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  CreateMqttGatewayRequest,
  ListMqttGatewaysResponse,
  MqttGatewayBundle,
  MqttGatewayView,
  RevokeMqttGatewayRequest,
} from '@lodex-pro/shared/validation/mqtt-gateways';

export const MQTT_GATEWAYS_KEY = (tenantId: string): readonly unknown[] => [
  'platform',
  'mqtt-gateways',
  tenantId,
];

const base = (tenantId: string): string =>
  `/v1/platform/operator-companies/${tenantId}/mqtt-gateways`;

export function useMqttGateways(
  tenantId: string,
): ReturnType<typeof useApiQuery<ListMqttGatewaysResponse>> {
  const opts = useApiOpts();
  return useApiQuery<ListMqttGatewaysResponse>(opts, base(tenantId), {
    queryKey: MQTT_GATEWAYS_KEY(tenantId),
    staleTime: 15_000,
  });
}

/** Refetch the list after any change. */
export function useRefreshMqttGateways(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: MQTT_GATEWAYS_KEY(tenantId) });
}

export function useAddMqttGateway(
  tenantId: string,
): ReturnType<typeof useApiMutation<CreateMqttGatewayRequest, MqttGatewayBundle>> {
  const opts = useApiOpts();
  return useApiMutation<CreateMqttGatewayRequest, MqttGatewayBundle>(opts, base(tenantId), 'POST');
}

/** Revoke, rotate and finish one gateway: the path carries its id, so these take it at call time. */
export function useRevokeMqttGateway(
  tenantId: string,
  id: string,
): ReturnType<
  typeof useApiMutation<RevokeMqttGatewayRequest, { certificatesDeactivated: number }>
> {
  const opts = useApiOpts();
  return useApiMutation<RevokeMqttGatewayRequest, { certificatesDeactivated: number }>(
    opts,
    `${base(tenantId)}/${id}/revoke`,
    'POST',
  );
}

export function useRotateMqttGateway(
  tenantId: string,
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, MqttGatewayBundle>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, MqttGatewayBundle>(
    opts,
    `${base(tenantId)}/${id}/rotate`,
    'POST',
  );
}

export function useFinishMqttRotation(
  tenantId: string,
  id: string,
): ReturnType<typeof useApiMutation<Record<string, never>, { certificateDeactivated: boolean }>> {
  const opts = useApiOpts();
  return useApiMutation<Record<string, never>, { certificateDeactivated: boolean }>(
    opts,
    `${base(tenantId)}/${id}/rotate/finish`,
    'POST',
  );
}

/** A refused gateway action, in words. */
export function mqttGatewayError(err: unknown): string {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
  switch (code) {
    case 'FEATURE_NOT_ENABLED':
      return "MQTT tank readings (OP_MQTT) are not on this company's plan. Turn it on in the Plan card first.";
    case 'OPERATOR_MQTT_GATEWAY_LIMIT_REACHED':
      return 'This company has all the gateways its plan allows. Revoke one, or raise the OP_MQTT limit.';
    case 'OPERATOR_MQTT_ROTATION_IN_PROGRESS':
      return 'A rotation is already waiting. Finish it first.';
    case 'OPERATOR_MQTT_GATEWAY_NOT_LIVE':
      return 'That gateway is revoked or failed.';
    case 'OPERATOR_MQTT_PROVISION_FAILED':
      return 'AWS IoT did not finish setting up the gateway. Nothing was left active; try again.';
    default:
      return 'That did not work. Try again.';
  }
}

export const MQTT_STATUS_LABELS: Readonly<Record<MqttGatewayView['status'], string>> = {
  PENDING: 'Setting up',
  ACTIVE: 'Active',
  REVOKED: 'Revoked',
  FAILED: 'Failed',
};
